import React, { useState, useEffect, useRef } from "react";

// Map symbols to TradingView symbols
const getTradingViewSymbol = (item) => {
  if (!item) return "";

  const symbol = item.symbol.toUpperCase();

  // Crypto symbols - add USD pair
  const cryptoSymbols = [
    "BTC",
    "ETH",
    "USDT",
    "BNB",
    "SOL",
    "TON",
    "DOGE",
    "ADA",
    "NIGHT",
    "USDC",
    "TRX",
    "LINK",
    "HYPE",
    "XLM",
    "SUI",
    "BCH",
    "HBAR",
    "USDE",
    "AVAX",
    "LTC",
    "XRP",
  ];

  // usdt should be against usdc
  if (symbol === "USDT") return "BINANCE:USDCUSDT";

  if (symbol === "HYPE") return "BINANCE:HYPEUSDT.P";

  if (symbol === "NIGHT") return "BINANCE:NIGHTUSDT.P";

  if (cryptoSymbols.includes(symbol)) {
    return `BINANCE:${symbol}USDT`;
  }

  // Metals
  if (symbol === "XAU") return "TVC:GOLD"; // Gold
  if (symbol === "XAG") return "TVC:SILVER"; // Silver
  // Diamond - TradingView doesn't support diamond charts
  if (symbol === "XDI") return null; // Return null to trigger error message

  // Stocks - map to exchange symbols
  const stockMap = {
    MSFT: "NASDAQ:MSFT",
    AAPL: "NASDAQ:AAPL",
    NVDA: "NASDAQ:NVDA",
    GOOGL: "NASDAQ:GOOGL",
    AMZN: "NASDAQ:AMZN",
    META: "NASDAQ:META",
    TSLA: "NASDAQ:TSLA",
    JPM: "NYSE:JPM",
    AVGO: "NASDAQ:AVGO",
    V: "NYSE:V",
  };

  if (stockMap[symbol]) {
    return stockMap[symbol];
  }

  // Default fallback
  return symbol;
};

export default function TradingViewModal({ item, isOpen, onClose }) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("ModalTheme");
    return savedTheme || "light";
  });
  const containerRef = useRef(null);

  useEffect(() => {
    const handleThemeChange = () => {
      setTheme(localStorage.getItem("ModalTheme") || "light");
    };
    window.addEventListener("modalThemeChange", handleThemeChange);
    return () => window.removeEventListener("modalThemeChange", handleThemeChange);
  }, []);
  const scriptLoadedRef = useRef(false);

  // Load TradingView script
  useEffect(() => {
    if (!scriptLoadedRef.current && !window.TradingView) {
      const script = document.createElement("script");
      script.src = "https://s3.tradingview.com/tv.js";
      script.async = true;
      script.onload = () => {
        scriptLoadedRef.current = true;
        setScriptLoaded(true);
      };
      script.onerror = () => {
        setError("Failed to load TradingView script");
        console.error("Failed to load TradingView script");
      };
      document.body.appendChild(script);
    } else if (window.TradingView) {
      // Script already loaded
      scriptLoadedRef.current = true;
      setScriptLoaded(true);
    }
  }, []);

  // Reset loading state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsChartLoading(true);
    }
  }, [isOpen]);

  // Create widget when modal opens and script is loaded
  useEffect(() => {
    if (!isOpen || !item || !containerRef.current) {
      // Clear error when modal closes
      if (!isOpen) {
        setError(null);
      }
      return;
    }

    const createWidget = () => {
      setError(null);

      try {
        if (!window.TradingView) {
          setError("TradingView script not loaded");
          return;
        }

        // Clear previous widget
        containerRef.current.innerHTML = "";
        setError(null);

        const newKey = Date.now(); // Use timestamp for unique ID

        const tradingViewSymbol = getTradingViewSymbol(item);

        if (!tradingViewSymbol) {
          setIsChartLoading(false);
          if (item.symbol.toUpperCase() === "XDI") {
            setError(
              "Diamond charts are not available on TradingView. Please select another asset.",
            );
          } else {
            setError("Invalid symbol");
          }
          return;
        }

        // Set the container ID before creating widget
        containerRef.current.id = `tradingview_${newKey}`;

        new window.TradingView.widget({
          autosize: true,
          symbol: tradingViewSymbol,
          interval: "D",
          timezone: "Etc/UTC",
          theme: theme,
          style: "1",
          locale: "en",
          toolbar_bg: theme === "dark" ? "#1a1a1a" : "#ffffff",
          enable_publishing: false,
          hide_top_toolbar: false,
          hide_legend: false,
          save_image: false,
          container_id: `tradingview_${newKey}`,
          width: "100%",
          height: "100%",
        });

        // Chart renders asynchronously; hide loading after widget mounts
        setTimeout(() => setIsChartLoading(false), 1200);
      } catch (err) {
        console.error("Error creating TradingView widget:", err);
        setIsChartLoading(false);
        setError("Failed to load chart. Please try again.");
      }
    };

    // Wait for script to load if not already loaded
    if (scriptLoaded || window.TradingView) {
      createWidget();
    } else {
      // Check periodically if script loaded
      const checkInterval = setInterval(() => {
        if (window.TradingView) {
          setScriptLoaded(true);
          clearInterval(checkInterval);
          createWidget();
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.TradingView) {
          setError("TradingView script took too long to load");
        }
      }, 10000);

      return () => clearInterval(checkInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item, scriptLoaded, theme]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("ModalTheme", newTheme);
    window.dispatchEvent(new Event("modalThemeChange"));
  };

  if (!isOpen || !item) return null;

  return (
    <div className="project-modal-overlay" onClick={onClose}>
      <div
        className="project-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme === "light" ? "#ffffff" : "#000c",
          border: theme === "light" ? "1px solid #0002" : "1px solid #fff2",
        }}
      >
        <div
          className="project-modal-header"
          style={{
            background: theme === "light" ? "#f5f5f5" : "#000c",
            borderBottom:
              theme === "light" ? "1px solid #0002" : "1px solid #fff2",
          }}
        >
          <div
            className="project-modal-title"
            style={{
              color: theme === "light" ? "#000" : "#fff",
            }}
          >
            {item.name} ({item.symbol})
          </div>
          <div className="project-modal-actions">
            <button
              className="project-modal-btn refresh-btn"
              onClick={toggleTheme}
              title={
                theme === "light"
                  ? "Switch to Dark Theme"
                  : "Switch to Light Theme"
              }
              style={{
                borderColor: theme === "light" ? "#ccc" : "#fff4",
                color: theme === "light" ? "#000" : "#fff",
              }}
            >
              <i className={`fa fa-${theme === "light" ? "moon" : "sun"}`} />
            </button>
            <button
              className="project-modal-btn close-btn"
              onClick={onClose}
              title="Close"
              style={{
                borderColor: theme === "light" ? "#ccc" : "#fff4",
                color: theme === "light" ? "#000" : "#fff",
              }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        </div>
        <div className="project-modal-content" style={{ position: "relative" }}>
          {isChartLoading && !error && (
            <div className={`tradingview-modal-loading theme-${theme}`}>
              <i className="fa fa-chart-line tradingview-loading-icon" />
            </div>
          )}
          <div
            ref={containerRef}
            style={{
              width: "100%",
              height: "100%",
              display: error ? "none" : "block",
              position: "relative",
              zIndex: 2,
            }}
          />
          {error && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme === "light" ? "#000" : "#fff",
                flexDirection: "column",
                gap: "20px",
                background: theme === "light" ? "#ffffff" : "#000c",
              }}
            >
              <div style={{ fontSize: "18px" }}>{error}</div>
              <button
                onClick={() => {
                  setError(null);
                  setScriptLoaded(false);
                  scriptLoadedRef.current = false;
                }}
                style={{
                  padding: "10px 20px",
                  background: "transparent",
                  border:
                    theme === "light" ? "2px solid #ccc" : "2px solid #fff4",
                  color: theme === "light" ? "#000" : "#fff",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
